import React, { useEffect, useState } from 'react';
import { Card, Select, DatePicker, Row, Col, Table, Tag, Button, Spin } from 'antd';
import {
    FileTextOutlined,
    TrophyOutlined,
    ProjectOutlined,
    GiftOutlined,
    BarChartOutlined,
    PieChartOutlined,
    LineChartOutlined,
    AreaChartOutlined,
    FilterOutlined,
    ReloadOutlined,
    CalendarOutlined,
    CodeOutlined,
    PlusOutlined,
    MinusOutlined,
    UndoOutlined,
} from '@ant-design/icons';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import {
    fetchDashboardData,
    fetchRecentActivities,
    fetchPerformanceMetrics,
    fetchScriptNames,
} from '../../apis/dashboard.api';
import moment from 'moment';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    zoomPlugin,
);

const { RangePicker } = DatePicker;
const { Option } = Select;

export default function Home() {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        dateRange: 'today',
        script_name: null,
        startDate: null,
        endDate: null,
    });
    const [recentActivities, setRecentActivities] = useState({ data: [], totalCount: 0 });
    const [performanceMetrics, setPerformanceMetrics] = useState({
        data: [],
        totalCount: 0,
    });
    const [scriptNames, setScriptNames] = useState([]);
    const [activitiesPagination, setActivitiesPagination] = useState({
        pageNo: 0,
        limit: 10,
    });
    const [metricsPagination, setMetricsPagination] = useState({ pageNo: 0, limit: 10 });

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = { ...filters };
            if (filters.startDate && filters.endDate) {
                params.startDate = filters.startDate.format('YYYY-MM-DD');
                params.endDate = filters.endDate.format('YYYY-MM-DD');
            }
            const data = await fetchDashboardData(params);
            setDashboardData(data);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchActivitiesData = async (customPagination = null) => {
        try {
            const pagination = customPagination || activitiesPagination;
            const params = { ...filters, ...pagination };
            if (filters.startDate && filters.endDate) {
                params.startDate = filters.startDate.format('YYYY-MM-DD');
                params.endDate = filters.endDate.format('YYYY-MM-DD');
            }
            const data = await fetchRecentActivities(params);
            setRecentActivities(data);
        } catch (error) {
            console.error('Error fetching recent activities:', error);
        }
    };

    const fetchMetricsData = async (customPagination = null) => {
        try {
            const pagination = customPagination || metricsPagination;
            const params = { ...filters, ...pagination };
            if (filters.startDate && filters.endDate) {
                params.startDate = filters.startDate.format('YYYY-MM-DD');
                params.endDate = filters.endDate.format('YYYY-MM-DD');
            }
            const data = await fetchPerformanceMetrics(params);
            setPerformanceMetrics(data);
        } catch (error) {
            console.error('Error fetching performance metrics:', error);
        }
    };

    const fetchScriptNamesData = async () => {
        try {
            const data = await fetchScriptNames();
            setScriptNames(data);
        } catch (error) {
            console.error('Error fetching script names:', error);
        }
    };

    // Initial load on component mount
    useEffect(() => {
        fetchData();
        fetchActivitiesData();
        fetchMetricsData();
        fetchScriptNamesData();
    }, []);

    // Pagination effects only
    useEffect(() => {
        fetchActivitiesData();
    }, [activitiesPagination]);

    useEffect(() => {
        fetchMetricsData();
    }, [metricsPagination]);

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleDateRangeChange = (dates) => {
        if (dates) {
            setFilters((prev) => ({
                ...prev,
                startDate: dates[0],
                endDate: dates[1],
                dateRange: 'custom',
            }));
        } else {
            setFilters((prev) => ({
                ...prev,
                startDate: null,
                endDate: null,
                dateRange: 'all',
            }));
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spin size="large" />
            </div>
        );
    }

    if (!dashboardData) {
        return <div>No data available</div>;
    }

    return (
        <div className="p-6">
            {/* Header with Filters */}
            <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-2xl font-bold">Advanced Dashboard</h1>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => {
                            // Reset pagination to page 1 when refreshing
                            const resetPagination = { pageNo: 0, limit: 10 };
                            setActivitiesPagination(resetPagination);
                            setMetricsPagination(resetPagination);

                            // Fetch with reset pagination
                            fetchData();
                            fetchActivitiesData(resetPagination);
                            fetchMetricsData(resetPagination);
                        }}
                        loading={loading}
                    >
                        Refresh
                    </Button>
                </div>

                <Card className="mb-6">
                    <Row gutter={16} align="middle">
                        <Col span={filters.dateRange === 'custom' ? 6 : 8}>
                            <label className="block text-sm font-medium mb-2">
                                Date Range
                            </label>
                            <Select
                                value={filters.dateRange}
                                onChange={(value) => {
                                    handleFilterChange('dateRange', value);
                                }}
                                className="w-full"
                            >
                                <Option value="all">All Time</Option>
                                <Option value="today">Today</Option>
                                <Option value="yesterday">Yesterday</Option>
                                <Option value="this_week">This Week</Option>
                                <Option value="this_month">This Month</Option>
                                <Option value="custom">Custom Range</Option>
                            </Select>
                        </Col>
                        {filters.dateRange === 'custom' && (
                            <Col span={6}>
                                <label className="block text-sm font-medium mb-2">
                                    Custom Date Range
                                </label>
                                <RangePicker
                                    value={
                                        filters.startDate && filters.endDate
                                            ? [filters.startDate, filters.endDate]
                                            : null
                                    }
                                    onChange={handleDateRangeChange}
                                    className="w-full"
                                />
                            </Col>
                        )}
                        <Col span={filters.dateRange === 'custom' ? 6 : 8}>
                            <label className="block text-sm font-medium mb-2">
                                Script Name
                            </label>
                            <Select
                                placeholder="Select script name"
                                value={filters.script_name}
                                onChange={(value) =>
                                    handleFilterChange('script_name', value)
                                }
                                allowClear
                                showSearch
                                className="w-full"
                            >
                                {scriptNames.map((name) => (
                                    <Option key={name} value={name}>
                                        {name}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={filters.dateRange === 'custom' ? 6 : 8}>
                            <div className="flex items-end h-full">
                                <Button
                                    type="primary"
                                    icon={<FilterOutlined />}
                                    onClick={() => {
                                        // Reset pagination to page 1 when applying new filters
                                        const resetPagination = { pageNo: 0, limit: 10 };
                                        setActivitiesPagination(resetPagination);
                                        setMetricsPagination(resetPagination);

                                        // Fetch with reset pagination
                                        fetchData();
                                        fetchActivitiesData(resetPagination);
                                        fetchMetricsData(resetPagination);
                                    }}
                                    className="w-full"
                                >
                                    Apply Filters
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Card>
            </div>

            {/* Recent Activities Table */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24}>
                    <Card title="Recent Activities">
                        <RecentActivitiesTable
                            data={recentActivities.data}
                            pagination={activitiesPagination}
                            totalCount={recentActivities.totalCount}
                            onPageChange={(pageNo, limit) =>
                                setActivitiesPagination({ pageNo: pageNo - 1, limit })
                            }
                        />
                    </Card>
                </Col>
            </Row>

            {/* Performance Metrics Table */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24}>
                    <Card title="Performance Metrics">
                        <PerformanceMetricsTable
                            data={performanceMetrics.data}
                            pagination={metricsPagination}
                            totalCount={performanceMetrics.totalCount}
                            onPageChange={(pageNo, limit) =>
                                setMetricsPagination({ pageNo: pageNo - 1, limit })
                            }
                        />
                    </Card>
                </Col>
            </Row>

            {/* Main KPIs */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} sm={12} md={6}>
                    <KPICard
                        title="Total Tenders"
                        value={dashboardData.tenders}
                        icon={<FileTextOutlined />}
                        color="#1890ff"
                        trend={12.5}
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <KPICard
                        title="Total Contracts"
                        value={dashboardData.contracts}
                        icon={<TrophyOutlined />}
                        color="#52c41a"
                        trend={8.2}
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <KPICard
                        title="Total Projects"
                        value={dashboardData.projects}
                        icon={<ProjectOutlined />}
                        color="#fa8c16"
                        trend={-3.1}
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <KPICard
                        title="Total Grants"
                        value={dashboardData.grants}
                        icon={<GiftOutlined />}
                        color="#eb2f96"
                        trend={15.7}
                    />
                </Col>
            </Row>

            {/* Advanced Analytics KPIs */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} sm={12} md={6}>
                    <KPICard
                        title="Top Countries"
                        value={dashboardData.topCountriesCount}
                        icon={<BarChartOutlined />}
                        color="#722ed1"
                        subtitle="Active Countries"
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <KPICard
                        title="Active Customers"
                        value={dashboardData.activeCustomers}
                        icon={<CodeOutlined />}
                        color="#13c2c2"
                        subtitle="Registered Users"
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <KPICard
                        title="Top Sectors"
                        value={dashboardData.topSectorsCount}
                        icon={<AreaChartOutlined />}
                        color="#f5222d"
                        subtitle="Active Sectors"
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <KPICard
                        title="CPV Codes"
                        value={dashboardData.topCpvCodesCount}
                        icon={<PieChartOutlined />}
                        color="#52c41a"
                        subtitle="Active Codes"
                    />
                </Col>
            </Row>

            {/* Charts Section */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} lg={12}>
                    <Card title="Trend Analysis" className="h-96">
                        <LineChart data={dashboardData.trendData} />
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card title="Top Countries Distribution" className="h-96">
                        <BarChart data={dashboardData.advancedAnalytics?.topCountries} />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} lg={12}>
                    <Card title="Sector Distribution" className="h-96">
                        <PieChart data={dashboardData.advancedAnalytics?.topSectors} />
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card title="CPV Codes Distribution" className="h-96">
                        <DoughnutChart
                            data={dashboardData.advancedAnalytics?.topCpvCodes}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24}>
                    <Card title="Script Performance" className="h-[500px]">
                        <BarChart
                            data={dashboardData.advancedAnalytics?.scriptBreakdown}
                            height="h-[400px]"
                            enableZoom={true}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Script Analytics Section */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} sm={12} md={6}>
                    <KPICard
                        title="Active Scripts"
                        value={dashboardData.activeScripts}
                        icon={<CodeOutlined />}
                        color="#722ed1"
                        subtitle="Running Scripts"
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <KPICard
                        title="Funding Agencies"
                        value={
                            dashboardData.advancedAnalytics?.fundingAgencyDistribution
                                ?.length || 0
                        }
                        icon={<BarChartOutlined />}
                        color="#1890ff"
                        subtitle="Active Agencies"
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <KPICard
                        title="Competition Types"
                        value={
                            dashboardData.advancedAnalytics?.competitionAnalysis
                                ?.length || 0
                        }
                        icon={<TrophyOutlined />}
                        color="#52c41a"
                        subtitle="Different Types"
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <KPICard
                        title="Tender Types"
                        value={
                            dashboardData.advancedAnalytics?.tenderTypeAnalysis?.length ||
                            0
                        }
                        icon={<AreaChartOutlined />}
                        color="#f5222d"
                        subtitle="Live vs Archive"
                    />
                </Col>
            </Row>

            {/* Additional Analytics Charts */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} lg={12}>
                    <Card title="Funding Agency Distribution" className="h-96">
                        <BarChart
                            data={
                                dashboardData.advancedAnalytics?.fundingAgencyDistribution
                            }
                        />
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card title="Competition Type Analysis" className="h-96">
                        <PieChart
                            data={dashboardData.advancedAnalytics?.competitionAnalysis}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} lg={12}>
                    <Card title="Tender Type Distribution" className="h-96">
                        <DoughnutChart
                            data={dashboardData.advancedAnalytics?.tenderTypeAnalysis}
                        />
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card title="Regional Distribution" className="h-96">
                        <BarChart
                            data={dashboardData.advancedAnalytics?.regionalDistribution}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

// KPI Card Component
function KPICard({ title, value, icon, color, trend, subtitle }) {
    return (
        <Card className="h-full">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-600 text-sm mb-1">{title}</p>
                    <p className="text-2xl font-bold" style={{ color }}>
                        {value}
                    </p>
                    {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
                    {trend !== undefined && (
                        <div className="flex items-center mt-1">
                            <span
                                className={`text-xs ${
                                    trend >= 0 ? 'text-green-500' : 'text-red-500'
                                }`}
                            >
                                {trend >= 0 ? '+' : ''}
                                {trend}%
                            </span>
                            <span className="text-xs text-gray-500 ml-2">
                                vs last period
                            </span>
                        </div>
                    )}
                </div>
                <div className="text-3xl" style={{ color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );
}

// Chart Components
function LineChart({ data }) {
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
            },
        },
    };

    return (
        <div className="h-64">
            <Line data={data} options={options} />
        </div>
    );
}

function BarChart({ data, height = 'h-64', enableZoom = false }) {
    const chartRef = React.useRef(null);

    const handleZoomIn = () => {
        if (chartRef.current) {
            chartRef.current.zoom(1.1);
        }
    };

    const handleZoomOut = () => {
        if (chartRef.current) {
            chartRef.current.zoom(0.9);
        }
    };

    const handleResetZoom = () => {
        if (chartRef.current) {
            chartRef.current.resetZoom();
        }
    };

    if (!data || !Array.isArray(data)) {
        return (
            <div className={`${height} flex items-center justify-center text-gray-500`}>
                No data available
            </div>
        );
    }

    const chartData = {
        labels: data.map(
            (item) =>
                item.name ||
                item.type ||
                item.code ||
                item.agency ||
                item.region ||
                'Unknown',
        ),
        datasets: [
            {
                label: 'Count',
                data: data.map((item) => item.count),
                backgroundColor: [
                    '#FF6384',
                    '#36A2EB',
                    '#FFCE56',
                    '#4BC0C0',
                    '#9966FF',
                    '#FF9F40',
                    '#FF6384',
                    '#C9CBCF',
                    '#4BC0C0',
                    '#FF6384',
                ],
                borderWidth: 1,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            ...(enableZoom && {
                zoom: {
                    zoom: {
                        wheel: {
                            enabled: true,
                        },
                        pinch: {
                            enabled: true,
                        },
                        mode: 'x',
                    },
                    pan: {
                        enabled: true,
                        mode: 'x',
                    },
                },
            }),
        },
        scales: {
            y: {
                beginAtZero: true,
            },
        },
    };

    return (
        <div className="relative">
            {enableZoom && (
                <div className="absolute top-2 right-2 z-10 flex gap-1">
                    <Button
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={handleZoomIn}
                        title="Zoom In"
                    />
                    <Button
                        size="small"
                        icon={<MinusOutlined />}
                        onClick={handleZoomOut}
                        title="Zoom Out"
                    />
                    <Button
                        size="small"
                        icon={<UndoOutlined />}
                        onClick={handleResetZoom}
                        title="Reset Zoom"
                    />
                </div>
            )}
            <div className={height}>
                <Bar ref={chartRef} data={chartData} options={options} />
            </div>
        </div>
    );
}

function PieChart({ data }) {
    if (!data || !Array.isArray(data)) {
        return (
            <div className="h-64 flex items-center justify-center text-gray-500">
                No data available
            </div>
        );
    }

    const chartData = {
        labels: data.map((item) => item.name || item.type || 'Unknown'),
        datasets: [
            {
                label: 'Distribution',
                data: data.map((item) => item.count),
                backgroundColor: [
                    '#FF6384',
                    '#36A2EB',
                    '#FFCE56',
                    '#4BC0C0',
                    '#9966FF',
                    '#FF9F40',
                    '#FF6384',
                    '#C9CBCF',
                ],
                borderWidth: 2,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
            },
        },
    };

    return (
        <div className="h-64">
            <Pie data={chartData} options={options} />
        </div>
    );
}

function DoughnutChart({ data }) {
    if (!data || !Array.isArray(data)) {
        return (
            <div className="h-64 flex items-center justify-center text-gray-500">
                No data available
            </div>
        );
    }

    const chartData = {
        labels: data.map((item) => item.code || item.type || 'Unknown'),
        datasets: [
            {
                data: data.map((item) => item.count),
                backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'],
                borderWidth: 2,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
            },
        },
    };

    return (
        <div className="h-64">
            <Doughnut data={chartData} options={options} />
        </div>
    );
}

// Table Components
function RecentActivitiesTable({ data, pagination, totalCount, onPageChange }) {
    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            ellipsis: true,
            render: (text) => (
                <div className="max-w-xs truncate" title={text}>
                    {text}
                </div>
            ),
        },
        {
            title: 'Authority',
            dataIndex: 'authority',
            key: 'authority',
            ellipsis: true,
        },
        {
            title: 'Country',
            dataIndex: 'country',
            key: 'country',
            render: (country) => <Tag color="blue">{country}</Tag>,
        },
        {
            title: 'Script',
            dataIndex: 'script',
            key: 'script',
            render: (script) => <Tag color="green">{script}</Tag>,
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (date) => moment(date).format('MMM DD, YYYY'),
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={data}
            pagination={{
                current: pagination.pageNo + 1,
                pageSize: pagination.limit,
                total: totalCount,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} items`,
                onChange: onPageChange,
            }}
            size="small"
            rowKey="id"
        />
    );
}

function PerformanceMetricsTable({ data, pagination, totalCount, onPageChange }) {
    const columns = [
        {
            title: 'Script Name',
            dataIndex: 'script',
            key: 'script',
            render: (script) => <Tag color="purple">{script}</Tag>,
        },
        {
            title: 'Total Tenders',
            dataIndex: 'totalTenders',
            key: 'totalTenders',
            sorter: (a, b) => a.totalTenders - b.totalTenders,
        },
        {
            title: 'Last Run',
            dataIndex: 'lastRun',
            key: 'lastRun',
            render: (date) => moment(date).format('MMM DD, YYYY HH:mm'),
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={data}
            pagination={{
                current: pagination.pageNo + 1,
                pageSize: pagination.limit,
                total: totalCount,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} items`,
                onChange: onPageChange,
            }}
            size="small"
            rowKey="script"
        />
    );
}
